/**
 * Utilidades para trabajar con localStorage
 * Proporciona funciones para guardar y recuperar datos del almacenamiento local
 * con manejo de errores y soporte para tipos
 */

/**
 * Guarda un valor en localStorage
 * @param key Clave para almacenar el valor
 * @param value Valor a almacenar (se convertirá a JSON)
 * @returns true si se guardó correctamente, false si hubo un error
 */
export const saveToLocalStorage = <T>(key: string, value: T): boolean => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error saving to localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Recupera un valor de localStorage
 * @param key Clave del valor a recuperar
 * @param defaultValue Valor por defecto si no existe la clave o hay un error
 * @returns El valor recuperado o el valor por defecto
 */
export const getFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error getting from localStorage (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Elimina un valor de localStorage
 * @param key Clave del valor a eliminar
 * @returns true si se eliminó correctamente, false si hubo un error
 */
export const removeFromLocalStorage = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Verifica si existe una clave en localStorage
 * @param key Clave a verificar
 * @returns true si existe, false si no existe o hay un error
 */
export const existsInLocalStorage = (key: string): boolean => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error(`Error checking localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Limpia todo el localStorage
 * @returns true si se limpió correctamente, false si hubo un error
 */
export const clearLocalStorage = (): boolean => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};
